import { Alert, App, Form } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CustomActionButtons,
  CustomDatePicker,
  CustomInput,
  CustomModal,
  CustomSelect,
} from "sipli-fleet-core/components";
import {
  useMutationCreateTachographCard,
  useMutationUpdateTachographCard,
  useQueryGetTachographCardById,
  useQueryGetTachographCompanyLookup,
} from "src/queries/tachographCard.queries";

interface TachographCardAddEditProps {
  open: boolean;
  cardId: string | null;
  onClose: () => void;
}

interface TachographCardFormValues {
  smartCardId?: string;
  expireDate?: Dayjs | null;
  customerId?: string | number;
}

const parseExpireDate = (value: string | number | null | undefined): Dayjs | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numericValue = Number(value);

  if (Number.isFinite(numericValue)) {
    const date = numericValue < 1e11 ? dayjs.unix(numericValue) : dayjs(numericValue);

    return date.isValid() ? date : null;
  }

  const date = dayjs(value);
  return date.isValid() ? date : null;
};

const TachographCardAddEdit = ({ open, cardId, onClose }: TachographCardAddEditProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<TachographCardFormValues>();

  const isEditing = Boolean(cardId);
  const { message } = App.useApp();
  const detailQuery = useQueryGetTachographCardById(cardId, open && isEditing);

  const createMutation = useMutationCreateTachographCard();
  const updateMutation = useMutationUpdateTachographCard();

  const clientLookupQuery = useQueryGetTachographCompanyLookup(open);

  const record = detailQuery.data?.data?.response;

  const clientOptions = useMemo(() => {
    const payload = clientLookupQuery.data?.data;
    const clients = Array.isArray(payload) ? payload : payload?.response;

    return (Array.isArray(clients) ? clients : []).map((client) => ({
      label: client.name,
      value: client.id,
    }));
  }, [clientLookupQuery.data]);

  useEffect(() => {
    if (!open) return;

    if (isEditing && !record) {
      form.resetFields();
      return;
    }

    form.setFieldsValue({
      smartCardId: record?.smartCardId ?? "",
      expireDate: parseExpireDate(record?.expireDate),
      customerId: record?.client?.id,
    });
  }, [open, isEditing, record, form]);

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const isLoading = (isEditing && detailQuery.isFetching) || clientLookupQuery.isFetching;

  const hasLoadError = (isEditing && detailQuery.isError) || clientLookupQuery.isError;

  const isDisabled = isLoading || isSaving || hasLoadError || (isEditing && !record);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = async () => {
    if (isDisabled) return;

    const values = await form.validateFields().catch(() => null);

    if (!values || values.customerId === undefined) return;

    const selectedExpireDate =
      values.expireDate ?? (isEditing ? parseExpireDate(record?.expireDate) : null);

    const expireDate = selectedExpireDate?.unix() ?? null;

    try {
      if (isEditing) {
        const currentCardId = record?.cardId ?? cardId;

        if (!currentCardId) return;

        await updateMutation.mutateAsync({
          cardId: currentCardId,
          smartCardId: values.smartCardId ?? record?.smartCardId ?? "",
          expireDate,
          customerId: values.customerId,
        });

        message.success(t("card_updated_successfully"));
      } else {
        if (expireDate === null) {
          form.setFields([
            {
              name: "expireDate",
              errors: [t("expire_date")],
            },
          ]);
          return;
        }

        await createMutation.mutateAsync({
          smartCardId: values.smartCardId,
          expireDate,
          customerId: values.customerId,
        });

        message.success(t("card_added_successfully"));
      }

      handleClose();
    } catch {
      message.error(t("error"));
    }
  };

  return (
    <CustomModal
      open={open}
      title={t(isEditing ? "edit_tachograph_card" : "add_tachograph_card")}
      onCancel={handleClose}
      destroyOnHidden
      width={620}
      footer={null}
      centered
    >
      {hasLoadError && <Alert type="error" showIcon message={t("error")} />}

      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        className="grid grid-cols-1 gap-x-4 pt-4 sm:grid-cols-2"
        disabled={isDisabled}
        onFinish={handleSubmit}
      >
        <Form.Item name="smartCardId" label={t("smart_card_id")} className="mb-3! sm:col-span-2">
          <CustomInput className="common-input" placeholder={t("smart_card_id")} />
        </Form.Item>

        {isEditing && (
          <Form.Item label={t("external_id")} className="mb-3! sm:col-span-2">
            <CustomInput
              className="common-input"
              value={record?.externalId ?? ""}
              readOnly
              disabled
            />
          </Form.Item>
        )}

        <Form.Item
          name="customerId"
          label={t("select_customer")}
          className="mb-3! sm:col-span-2"
          rules={[
            {
              required: true,
              message: t("select_customer"),
            },
          ]}
        >
          <CustomSelect
            className="common-select"
            options={clientOptions}
            placeholder={t("select_customer")}
            loading={clientLookupQuery.isFetching}
            showSearch
            optionFilterProp="label"
            allowClear
          />
        </Form.Item>

        <Form.Item name="expireDate" label={t("expire_date")} className="mb-3!">
          <CustomDatePicker className="common-datepicker" placeholder={t("expire_date")} />
        </Form.Item>

        <div className="pt-3 sm:col-span-2">
          <CustomActionButtons
            onCancel={handleClose}
            onOk={handleSubmit}
            okBtnLabel={isEditing ? "update" : "add"}
            loading={isSaving}
            disabled={isDisabled}
          />
        </div>
      </Form>
    </CustomModal>
  );
};

export default TachographCardAddEdit;
